import React from "react";
import { useSelector } from "react-redux";
import { AppState } from "store";
import { useLocation, useParams } from "react-router-dom";
//#region types
import { EntityIdParams } from "models/propTypes/urlParamsTypes";
import { WintSuperkollAPIModelTimeReporting } from "wint-api-client/dist/types";
import { RouteConstants } from "utils/constants";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { BreadCrumb, LoadingWithLogo, PageLayouts } from "components/common";
import { ErrorView } from "components/error";
import TimeReportingMonthDetailsView from "./detailsView";
import { useCompanyService, useTimeReportingService } from "serviceHooks";
//#endregion
import { DateHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";

const TimeReportingMonthView: React.FC = () => {
  const companyService = useCompanyService();
  const timeReportingService = useTimeReportingService();

  const [loading, setLoading] = React.useState(true);
  const queryParams = new URLSearchParams(useLocation().search);
  const urlParams = useParams<EntityIdParams>();
  const [loggedInUser] = useSelector((state: AppState) => [state.auth.loggedInUser]);
  const today = new Date();
  const [data, setData] = React.useState<WintSuperkollAPIModelTimeReporting.TimeReportMetaDataWithPeriods>();
  const [personId, setPersonId] = React.useState<number>();

  React.useEffect(() => {
    setLoading(true);
    let employeeId = loggedInUser.personId;
    if (queryParams.has(RouteConstants.TimeReportingQueryParams.EmployeeId)) {
      employeeId = Number(queryParams.get(RouteConstants.TimeReportingQueryParams.EmployeeId));
    }
    setPersonId(employeeId);
    const date = new Date(queryParams.get(RouteConstants.TimeReportingQueryParams.CurrentDate) ?? today);
    initialLoad(employeeId, date);
  }, [urlParams]);

  const initialLoad = (employeeId: number, date: Date) => {
    timeReportingService
      .filter(employeeId, DateHelper.getFirstDayOfMonth(date), DateHelper.getLastDayOfMonth(date))
      .then((res) => {
        setData(res);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const hasPermission = React.useMemo(() => {
    return !!personId && companyService.permissionToModifyOtherPersonsTimeReports(personId);
  }, [personId]);

  return (
    <PageLayouts.EntityFullScreen
      id={0}
      entityKey={`timeReportingMonth`}
      navigation={
        <BreadCrumb
          prevItems={[
            {
              text: data?.employeeName ?? "",
              href: UrlHelper.formatPath(`/Employee/${data?.employeeId}`),
            },
          ]}
          current={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.TimeReport)}
          showHome
        />
      }
      hasPermissions={hasPermission}
    >
      {loading ? <LoadingWithLogo /> : data ? <TimeReportingMonthDetailsView metaData={data} /> : <ErrorView />}
    </PageLayouts.EntityFullScreen>
  );
};

export default TimeReportingMonthView;
